import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Grid, Card, CardContent, Paper, useTheme, useMediaQuery } from '@mui/material';
import { TheaterComedy, School, Group, Payment, Event, Security } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import getAppConfig from '../config/app-config';
import ModalFirstAccess from '../components/ModalFirstAccess';

const Home = () => {
  const config = getAppConfig();

  // Verifica che le configurazioni necessarie siano presenti
  if (!config.BRAND_NAME || !config.HOME_IMAGE) {
    throw new Error('BRAND_NAME o HOME_IMAGE non sono definite nella configurazione.');
  }

  const { isAuthenticated, user, isProd, isDev } = useAuth();
  const isRockdreamsProduction = config.ASSOCIATE === 'rockdreams' && (isProd || isDev);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isFirstLogin = Boolean(user?.isFirstLogin);
  const showWallsButton = isRockdreamsProduction;
  const showAuthButtons = !isAuthenticated;

  const authButtonSx = {
    color: 'white',
    borderColor: 'white',
  };
  const features = [
    {
      icon: <TheaterComedy sx={{ fontSize: 40, color: 'primary.light' }} />,
      title: 'Gestione Corsi',
      description: 'Iscriviti ai nostri corsi e sviluppa le tue competenze.',
    },
    {
      icon: <Group sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Membri',
      description: "Diventa membro dell'associazione e accedi a tutti i servizi esclusivi.",
    },
    {
      icon: <Payment sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Pagamenti',
      description: 'Gestisci facilmente le tue quote associative e i pagamenti dei corsi.',
    },
    {
      icon: <Event sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Eventi',
      description: "Partecipa agli eventi e alle attività dell'associazione.",
    },
    {
      icon: <Security sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Sicurezza',
      description: 'I tuoi dati sono protetti e gestiti in modo sicuro e riservato.',
    },
    {
      icon: <School sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Formazione',
      description: 'Accedi a contenuti formativi e risorse per il tuo sviluppo.',
    },
  ];


  let heroText = '';
  if (isRockdreamsProduction) {
    heroText = 'Benvenuti! In questo sito potrai avere accesso alla lista delle vie su corda delle pareti esterne. Se ti registri potrai inserire il grado proposto e tenere traccia dei tuoi progressi.';
  } else if (!isAuthenticated) {
    heroText = `Benvenuto! Registrati come utente o loggati come docente o amministratore.`;
  } else if (user?.roles?.includes('admin')) {
    heroText = `Benvenuto amministratore! Da questa piattaforma puoi gestire utenti, corsi, iscrizioni, pagamenti e tutte le attività dell'associazione ${config.BRAND_NAME}.`;
  } else if (user?.roles?.includes('teacher')) {
    heroText = `Benvenuto docente! Qui puoi gestire i tuoi corsi, visualizzare gli iscritti, segnare le presenze e comunicare con i membri dell'associazione ${config.BRAND_NAME}.`;
  } else if (user?.roles?.includes('member')) {
    heroText = `Benvenuto socio! Accedi ai tuoi corsi, rinnova la tessera, partecipa agli eventi e resta aggiornato sulle attività dell'associazione ${config.BRAND_NAME}.`;
  }

  return (
    <Box mt={2}>
      {/* Hero Section */}
      <Paper
        sx={{
          borderRadius: 2,
          position: 'relative',
          color: '#fff',
          mb: 4,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundImage: `url(${config.HOME_IMAGE})`,
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            borderRadius: 2,
            top: 0,
            bottom: 0,
            right: 0,
            left: 0,
            backgroundColor: 'rgba(0,0,0,.2)',
          }}
        />
        <Container maxWidth='lg' sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography 
                component='h1' 
                variant={isMobile ? 'h3' : 'h2'} 
                color='inherit' 
                gutterBottom 
                sx={{ 
                  fontWeight: 'bold',
                  textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 0 4px #000'
                }}
              >
                {config.BRAND_NAME}
              </Typography>
              <Box
                sx={{
                  p: { xs: 2, md: 3 },
                  borderRadius: 2,
                  display: 'inline-block',
                  mb: 2,
                  maxWidth: { xs: '100%', md: '80%' },
                }}
              >
                <Typography 
                  variant={isMobile ? 'h6' : 'h5'} 
                  color='#fff' 
                  fontWeight='bold' 
                  sx={{ 
                    m: 0,
                    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 0 4px #000'
                  }}
                >
                  {heroText}
                </Typography>
              </Box>
              <Box
                sx={{
                  mt: 4,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {showWallsButton && (
                  <Button
                    variant='contained'
                    size='large'
                    onClick={() => navigate('/walls')}
                    sx={{ color: 'white', borderColor: 'white' }}
                  >
                    Elenco Vie
                  </Button>
                )}

                {showAuthButtons && (
                  <Button
                    variant='contained'
                    size='large'
                    onClick={() => navigate('/register')}
                    sx={authButtonSx}
                  >
                    Registrati
                  </Button>
                )}
                {showAuthButtons && (
                  <Button
                    variant='contained'
                    size='large'
                    onClick={() => navigate('/login')}
                    sx={authButtonSx}
                  >
                    Accedi
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Paper>

      {/* Features Section */}
      {!isRockdreamsProduction && (
        <Container maxWidth='lg' sx={{ py: { xs: 4, md: 8 }, backgroundColor: 'white', p: 3, borderRadius: 2 }}>
          <Typography component='h2' variant={isMobile ? 'h4' : 'h3'} align='center' color='text.primary' gutterBottom sx={{ mb: { xs: 3, md: 6 } }}>
            I Nostri Servizi
          </Typography>
          <Grid container spacing={{ xs: 2, md: 4 }} alignItems='stretch' borderRadius={2} border={1} borderColor='grey.400'>
            {features.map((feature, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index} sx={{ display: 'flex' }}>
                <Card
                  sx={{
                    flexGrow: 1,
                    height: '100%',
                    maxWidth: 360,
                    mx: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                    <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                    <Typography gutterBottom variant='h6' component='h3'>
                      {feature.title}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      )}

      {/* First Access Modal */}
      <ModalFirstAccess 
        open={isFirstLogin}  
      />
    </Box>
  );
};

export default Home;
